import React, { ReactElement, useEffect, useMemo } from 'react';
import { ZCDSelect } from '@zencity/common-ui';
import { SelectOption } from '@zencity/common-ui/lib/ZCD/ZCDFilter/types';
import { useTranslation } from 'react-i18next';
import { FieldTitle } from 'screens/WidgetGenerator/components/FieldTitle/FieldTitle';
import { ReportFilterProps } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import { SurveyGroup } from '@zencity/survey-types';
import { surveyGroupLabel } from 'screens/WidgetGenerator/reportGeneratorComponents/helpers/surveyGroup';

interface SurveyGroupById {
  [surveyGroupId: number]: SurveyGroup;
}

const convertSurveyGroupToOption = (instance: SurveyGroup): SelectOption => ({
  value: instance.id,
  label: surveyGroupLabel(instance),
});

export const SurveyGroupFilter = (props: ReportFilterProps<number>): ReactElement | null => {
  const { value, onChange, clientData } = props;
  const { t: translate } = useTranslation();

  const clientSurveysGroups = useMemo<SurveyGroupById>(() => {
    if (!clientData.surveysGroups) {
      return {};
    }
    return clientData.surveysGroups.reduce((accum, item) => ({ ...accum, [item.id]: item }), {} as SurveyGroupById);
  }, [clientData.surveysGroups]);

  const handleSurveyGroupSelection = (newValue: unknown) => {
    const selectedValue = newValue as SelectOption;
    onChange(selectedValue.value as number);
  };

  const surveysGroupsOptions = useMemo<SelectOption[]>(
    () => Object.values(clientSurveysGroups).map(convertSurveyGroupToOption),
    [clientSurveysGroups],
  );

  const filterValue = useMemo(() => {
    if (!value) {
      return undefined;
    }
    const selectedSurveyGroup = clientSurveysGroups[value];
    if (!selectedSurveyGroup) {
      return undefined;
    }

    return convertSurveyGroupToOption(selectedSurveyGroup);
  }, [clientSurveysGroups, value]);

  useEffect(() => {
    setTimeout(() => {
      // Select first survey group by default.
      const needToSetDefaultValue = !value && surveysGroupsOptions && surveysGroupsOptions.length > 0;
      if (needToSetDefaultValue) {
        onChange(surveysGroupsOptions[0].value as number);
      }
    }, 0);
  }, [clientSurveysGroups, surveysGroupsOptions, value, onChange]);

  return (
    <>
      <FieldTitle title={translate('widgetGenerator.selectSurveyGroup')} />
      <ZCDSelect
        placeholder={translate('widgetGenerator.selectSurveyGroup')}
        options={surveysGroupsOptions}
        onChange={handleSurveyGroupSelection}
        value={filterValue}
      />
    </>
  );
};
